import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronDown, ChevronRight, Lock, Info, Globe } from 'lucide-react';
import { formatCurrency } from '@/lib/budget-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { mockGlobalCategories, mockGlobalSubcategories, getGlobalSubcategoriesByCategory } from '@/lib/mock-data/category-mock-data';
import type { CostBudget, CostCategory, CostSubcategory } from '@/types/budget.types';

interface EditCostBudgetFormProps {
  costBudget: CostBudget;
  onChange: (changes: CostBudget) => void;
}

export function EditCostBudgetForm({ costBudget, onChange }: EditCostBudgetFormProps) {
  const [categories, setCategories] = useState<CostCategory[]>(
    costBudget?.categories || []
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [showAddGlobalDialog, setShowAddGlobalDialog] = useState(false);
  const [selectedGlobalCategory, setSelectedGlobalCategory] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState<string>('labour');

  // Calculate totals whenever categories change
  useEffect(() => {
    const totals = calculateTotals(categories);
    onChange({
      ...costBudget,
      categories,
      totals,
    });
  }, [categories]);

  const calculateTotals = (cats: CostCategory[]) => {
    const budget = cats.reduce((sum, cat) => sum + (cat.budget || 0), 0);
    const actual = cats.reduce((sum, cat) => sum + (cat.actual || 0), 0);
    const committed = cats.reduce((sum, cat) => sum + (cat.committed || 0), 0);
    const available = budget - actual - committed;
    const progressPercent = budget > 0 ? ((actual + committed) / budget) * 100 : 0;

    return {
      budget,
      actual,
      committed,
      available,
      progressPercent,
    };
  };

  const handleCategoryChange = (index: number, field: keyof CostCategory, value: any) => {
    const updated = [...categories];
    
    // Don't allow editing global categories
    if (updated[index].scope === 'global' && field === 'name') {
      return;
    }
    
    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    // Recalculate subcategory totals if this is a parent category
    if (updated[index].subcategories && updated[index].subcategories!.length > 0) {
      const subBudget = updated[index].subcategories!.reduce(
        (sum, sub) => sum + (sub.budget || 0),
        0
      );
      updated[index].budget = subBudget;
    }

    setCategories(updated);
  };

  const handleSubcategoryChange = (
    categoryIndex: number,
    subIndex: number,
    field: string,
    value: any
  ) => {
    const updated = [...categories];
    const subcategories = [...(updated[categoryIndex].subcategories || [])];
    
    // Don't allow editing global subcategories
    if (subcategories[subIndex].scope === 'global' && field === 'name') {
      return;
    }
    
    subcategories[subIndex] = {
      ...subcategories[subIndex],
      [field]: value,
    };
    updated[categoryIndex].subcategories = subcategories;

    // Update parent category budget
    const subBudget = subcategories.reduce((sum, sub) => sum + (sub.budget || 0), 0);
    updated[categoryIndex].budget = subBudget;

    setCategories(updated);
  };

  const addGlobalCategory = () => {
    if (!selectedGlobalCategory) return;
    
    const globalCat = mockGlobalCategories.find(c => c.id === selectedGlobalCategory);
    if (!globalCat) return;

    // Check if already added
    if (categories.some(c => c.globalCategoryId === globalCat.id)) {
      alert('This global category is already added to the budget.');
      return;
    }

    const globalSubs = getGlobalSubcategoriesByCategory(globalCat.id);
    
    const newCategory: CostCategory = {
      id: `cat-${Date.now()}`,
      name: globalCat.name,
      type: globalCat.type,
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'global',
      globalCategoryId: globalCat.id,
      isCustom: false,
      subcategories: globalSubs.map(sub => ({
        id: `sub-${Date.now()}-${sub.id}`,
        name: sub.name,
        budget: 0,
        actual: 0,
        committed: 0,
        available: 0,
        progressPercent: 0,
        scope: 'global',
        globalSubcategoryId: sub.id,
        isCustom: false,
      })),
    };
    
    setCategories([...categories, newCategory]);
    setShowAddGlobalDialog(false);
    setSelectedGlobalCategory('');
  };

  const addProjectCategory = () => {
    if (!newCategoryName.trim()) return;

    const newCategory: CostCategory = {
      id: `cat-${Date.now()}`,
      name: newCategoryName,
      type: newCategoryType as any,
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'project',
      isCustom: true,
    };
    
    setCategories([...categories, newCategory]);
    setShowAddCategoryDialog(false);
    setNewCategoryName('');
    setNewCategoryType('labour');
  };

  const addSubcategory = (categoryIndex: number) => {
    const updated = [...categories];
    const category = updated[categoryIndex];
    
    // For global categories, only allow adding from global subcategories
    if (category.scope === 'global') {
      alert('Global categories use predefined subcategories. To add more, manage them in Category Management settings.');
      return;
    }
    
    const subcategories = updated[categoryIndex].subcategories || [];
    subcategories.push({
      id: `sub-${Date.now()}`,
      name: 'New Subcategory',
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'project',
      isCustom: true,
    });
    updated[categoryIndex].subcategories = subcategories;
    setCategories(updated);
    
    // Expand the category to show the new subcategory
    setExpandedCategories(new Set([...expandedCategories, updated[categoryIndex].id]));
  };

  const removeCategory = (index: number) => {
    const category = categories[index];
    
    // Warn if trying to remove a global category
    if (category.scope === 'global') {
      if (!confirm(`Remove "${category.name}" from this budget? This won't delete the global category.`)) {
        return;
      }
    }
    
    const updated = categories.filter((_, i) => i !== index);
    setCategories(updated);
  };

  const removeSubcategory = (categoryIndex: number, subIndex: number) => {
    const updated = [...categories];
    const subcategory = updated[categoryIndex].subcategories?.[subIndex];
    
    if (subcategory?.scope === 'global') {
      if (!confirm(`Remove "${subcategory.name}" from this budget? This won't delete the global subcategory.`)) {
        return;
      }
    }
    
    const subcategories = (updated[categoryIndex].subcategories || []).filter(
      (_, i) => i !== subIndex
    );
    updated[categoryIndex].subcategories = subcategories;
    
    // Update parent category budget
    const subBudget = subcategories.reduce((sum, sub) => sum + (sub.budget || 0), 0);
    updated[categoryIndex].budget = subBudget;
    
    setCategories(updated);
  };

  const toggleCategory = (categoryId: string) => {
    const updated = new Set(expandedCategories);
    if (updated.has(categoryId)) {
      updated.delete(categoryId);
    } else {
      updated.add(categoryId);
    }
    setExpandedCategories(updated);
  };

  const totals = calculateTotals(categories);

  // Get available global categories (not already added)
  const availableGlobalCategories = mockGlobalCategories.filter(
    gc => !categories.some(c => c.globalCategoryId === gc.id)
  );

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Info Banner */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900">
                  Hybrid Category Model
                </p>
                <p className="text-sm text-blue-800">
                  <strong className="inline-flex items-center gap-1">
                    <Globe className="h-3 w-3" /> Global categories
                  </strong>{' '}
                  (read-only names, editable budgets) are managed in Settings and shared across projects.{' '}
                  <strong>Project-specific categories</strong> (fully editable) are unique to this project.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cost Budget Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Total Budget</Label>
                <div className="text-2xl font-bold">{formatCurrency(totals.budget)}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Actual</Label>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totals.actual)}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Committed</Label>
                <div className="text-2xl font-bold text-amber-600">
                  {formatCurrency(totals.committed)}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Available</Label>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totals.available)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Budget Categories</CardTitle>
            <div className="flex gap-2">
              <Button onClick={() => setShowAddGlobalDialog(true)} size="sm" variant="outline">
                <Globe className="h-4 w-4 mr-2" />
                Add Global Category
              </Button>
              <Button onClick={() => setShowAddCategoryDialog(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Project Category
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {categories.map((category, index) => (
              <div key={category.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  {category.subcategories && category.subcategories.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => toggleCategory(category.id)}
                    >
                      {expandedCategories.has(category.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  {(!category.subcategories || category.subcategories.length === 0) && <div className="w-8" />}
                  
                  <div className="flex-1 grid grid-cols-6 gap-3">
                    <div className="col-span-2">
                      <Label className="flex items-center gap-2">
                        Category Name
                        {category.scope === 'global' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-xs gap-1">
                                <Globe className="h-3 w-3" />
                                Global
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Global category - name is read-only</p>
                              <p className="text-xs text-muted-foreground">Manage in Settings</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {category.isCustom && (
                          <Badge variant="secondary" className="text-xs">
                            Project-Specific
                          </Badge>
                        )}
                      </Label>
                      <div className="flex items-center gap-2">
                        {category.scope === 'global' ? (
                          <div className="h-10 flex items-center text-sm font-medium">
                            {category.name}
                          </div>
                        ) : (
                          <Input
                            value={category.name}
                            onChange={(e) =>
                              handleCategoryChange(index, 'name', e.target.value)
                            }
                            placeholder="Category name"
                          />
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Budget</Label>
                      <Input
                        type="number"
                        value={category.budget || 0}
                        onChange={(e) =>
                          handleCategoryChange(index, 'budget', parseFloat(e.target.value) || 0)
                        }
                        disabled={category.subcategories && category.subcategories.length > 0}
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground">Actual</Label>
                      <div className="h-10 flex items-center text-sm">
                        {formatCurrency(category.actual || 0)}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground">Committed</Label>
                      <div className="h-10 flex items-center text-sm">
                        {formatCurrency(category.committed || 0)}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground">Available</Label>
                      <div className="h-10 flex items-center text-sm font-medium">
                        {formatCurrency(category.available || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {category.scope === 'project' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addSubcategory(index)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCategory(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                {/* Subcategories */}
                {expandedCategories.has(category.id) &&
                  category.subcategories &&
                  category.subcategories.map((sub, subIndex) => (
                    <div key={sub.id} className="ml-12 border-l-2 pl-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-6 gap-3">
                          <div className="col-span-2 flex items-center gap-2">
                            {sub.scope === 'global' ? (
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium">{sub.name}</div>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Globe className="h-3 w-3 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">Global subcategory</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            ) : (
                              <Input
                                value={sub.name}
                                onChange={(e) =>
                                  handleSubcategoryChange(index, subIndex, 'name', e.target.value)
                                }
                                placeholder="Subcategory name"
                                className="text-sm"
                              />
                            )}
                          </div>
                          
                          <div>
                            <Input
                              type="number"
                              value={sub.budget || 0}
                              onChange={(e) =>
                                handleSubcategoryChange(
                                  index,
                                  subIndex,
                                  'budget',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              placeholder="0"
                              className="text-sm"
                            />
                          </div>
                          
                          <div className="flex items-center text-sm text-muted-foreground">
                            {formatCurrency(sub.actual || 0)}
                          </div>
                          
                          <div className="flex items-center text-sm text-muted-foreground">
                            {formatCurrency(sub.committed || 0)}
                          </div>
                          
                          <div className="flex items-center text-sm font-medium">
                            {formatCurrency(sub.available || 0)}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubcategory(index, subIndex)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            ))}

            {categories.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No categories yet. Add global or project-specific categories to get started.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Global Category Dialog */}
        <Dialog open={showAddGlobalDialog} onOpenChange={setShowAddGlobalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Global Category</DialogTitle>
              <DialogDescription>
                Select a global category to add to this budget. Global categories are managed in Settings.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Category</Label>
                <Select value={selectedGlobalCategory} onValueChange={setSelectedGlobalCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a global category" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableGlobalCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <span>{cat.name}</span>
                          {cat.code && (
                            <span className="text-xs text-muted-foreground">({cat.code})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableGlobalCategories.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    All global categories have been added to this budget.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddGlobalDialog(false)}>
                Cancel
              </Button>
              <Button onClick={addGlobalCategory} disabled={!selectedGlobalCategory}>
                Add Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Project Category Dialog */}
        <Dialog open={showAddCategoryDialog} onOpenChange={setShowAddCategoryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Project-Specific Category</DialogTitle>
              <DialogDescription>
                Create a custom category unique to this project. This category will only be available in this project.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category Name</Label>
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Special Equipment"
                />
              </div>
              <div className="space-y-2">
                <Label>Category Type</Label>
                <Select value={newCategoryType} onValueChange={setNewCategoryType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labour">Labour</SelectItem>
                    <SelectItem value="expenses">Expenses</SelectItem>
                    <SelectItem value="subcontractors">Subcontractors</SelectItem>
                    <SelectItem value="overhead">Overhead</SelectItem>
                    <SelectItem value="contingency">Contingency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCategoryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={addProjectCategory} disabled={!newCategoryName.trim()}>
                Add Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
